import { AI_PAGE_ID_MAX_CHARS, AI_PAGE_ID_RE, AI_SLOT_ID_MAX_CHARS, AI_SLOT_ID_RE } from "@goppy/domain";
import { z } from "zod";

/**
 * 🔴 US-AIV2-0012 (@UC0151 · ADR-0009) — PHIẾU của Người phiên dịch: LLM chỉ được trả THỨ NÀY.
 *
 * Bất biến số 1 của v2 đúc vào cấu trúc: **Phiếu KHÔNG có ô số tiền, KHÔNG có id**. LLM đọc câu (đã cạo
 * PII), chọn đúng MỘT trang trong danh sách được phép, **gạch chân chữ** (span nguyên văn) cho từng ô,
 * nói độ chắc, và nếu kẹt giữa đúng hai trang thì đưa hai ứng viên để hỏi *A hay B*. Đổi chữ → số/người/
 * ngày/quỹ là việc của Người làm (0022, tất định); Phiếu chỉ mang CHỮ.
 *
 * Vì sao `.strict()` ở mọi tầng: một field `amountMinor` lỡ thêm vào đây là LLM lại "trả giá trị" như v1 —
 * và bug đó phải **không biểu diễn được**, không phải "bị review bắt". `envelope.test.ts` còn quét tên mọi
 * khoá trong schema (kể cả lồng) theo `AI_ENVELOPE_FORBIDDEN_KEY_RE`: thêm field số/id vào SCHEMA ⇒ đỏ.
 *
 * `start`/`end` là VỊ TRÍ KÝ TỰ trong câu gốc (để Người làm cắt đúng đoạn và mobile tô đậm) — không phải
 * con số nghiệp vụ; luật tên khoá vẫn áp cho chúng (không kết thúc bằng `Id`, không chứa `amount`/`Minor`).
 */

/** Bốn loại phiếu — tập ĐÓNG. `unknown` là câu trả lời hợp lệ (không bịa trang), `cancel` là tuyệt đối (`@UC0164`). */
export const AI_ENVELOPE_KINDS = ["page", "inFormAnswer", "cancel", "unknown"] as const;
export type AiEnvelopeKind = (typeof AI_ENVELOPE_KINDS)[number];

/**
 * Tên khoá CẤM trong Phiếu — bất kỳ tầng nào: số tiền (`amount`, `*Minor`, `price`, `total`), định danh
 * (`*Id`, `id`, `uuid`), và hai loại giá trị mà chỉ Người làm được sinh (`resolved`, `value`).
 * Đây là luật cho SCHEMA (meta-test), không phải cho dữ liệu runtime — dữ liệu runtime đã bị `.strict()` chặn.
 */
export const AI_ENVELOPE_FORBIDDEN_KEY_RE = /(amount|minor|price|total|money|cost|fee|vnd|balance|qty|count|^id$|Id$|uuid|resolved|^value$)/i;

/**
 * Khoá SỐ duy nhất được phép trong Phiếu (meta-test #804 M1 quét mọi `ZodNumber`/`ZodBigInt` ở mọi export của module):
 * vị trí ký tự của span và độ chắc. Một leaf số mang tên vô hại (`n`, `x`) vẫn đỏ nếu không nằm đây.
 */
export const AI_ENVELOPE_ALLOWED_NUMBER_KEYS = ["start", "end", "confidence"] as const;

/** Trần kích thước Phiếu (#804 L2): bảng append-only không xoá được — Phiếu là do LLM sinh, không cap là ghi mãi. */
export const AI_SPAN_TEXT_MAX_CHARS = 400;
/** Trần câu người dùng gõ một lượt — DTO `/ai/*` (0051) và dòng audit `input`/`result` dùng CÙNG hằng. */
export const AI_INPUT_MAX_CHARS = 4000;
export const AI_ENVELOPE_MAX_SPANS = 32;
export const AI_ENVELOPE_MAX_MULTI = 8;

/** Số ứng viên tối đa khi kẹt — "A hay B", không phải menu 5 lựa chọn (ADR: kẹt giữa ĐÚNG HAI trang). */
export const AI_ENVELOPE_MAX_AMBIGUITIES = 2;

/**
 * Một đoạn gạch chân: ô nào ↔ chữ nguyên văn nào, ở đâu trong câu.
 *
 * 🔴 US-AIV2-0054b (@UC0176 · QĐ PO 19/09 §A-BIS · review #881 M-5) — SPAN CÓ **HAI LOẠI**, và chúng nói hai
 * điều khác nhau về `[start,end)`. Trước 0054b chỉ có một loại, nên một câu docstring là đủ; nay không đủ nữa,
 * và Phiếu đi thẳng vào `ai_audit_log` — bảng **append-only, ngoài allow-list retention** ⇒ một lời khai sai
 * ghi vào đó thì **không sửa, không xoá được**. Nên loại span phải **tự khai**, không để người đọc suy từ Sổ.
 */
export const aiSpanSchema = z
  .object({
    slot: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE),
    /**
     * `mapped` vắng ⇒ chữ **NGUYÊN VĂN** từ câu (đã cạo PII) — không chuẩn hoá, không dịch.
     * `mapped: true` ⇒ chữ là **hằng của `choices`** trong Sổ, KHÔNG phải chuỗi con của câu.
     */
    text: z.string().min(1).max(AI_SPAN_TEXT_MAX_CHARS),
    /**
     * Vị trí ký tự [start, end) trong câu gốc; `end > start`.
     * `mapped` vắng ⇒ **`sentence.slice(start,end) === text`** (bất biến, `judgeSpan` ép).
     * `mapped: true` ⇒ **ĐOẠN CĂN CỨ**, luôn là **cả câu** `[0, sentence.length)` — KHÔNG phải nơi chứa `text`.
     */
    start: z.number().int().min(0),
    end: z.number().int().min(1),
    /**
     * 🔴 Span của ô **PHÂN LOẠI** (`type: "enum"`): giá trị được **ÁNH XẠ** vào tập đóng, nên nó **không nằm ở
     * đâu trong câu cả**. Trường này là thứ duy nhất phân biệt "model gạch chân" với "model phân loại" khi đọc
     * lại `ai_audit_log` sau này.
     *
     * Do **đường ống DẪN XUẤT** (`judgeSpan`), không phải do LLM khai: mọi span ra khỏi phép neo đều được dựng
     * lại từ đầu, nên một `mapped` do model tự nhét **không sống sót**. Để `optional` (không `default`) vì vắng
     * mặt là trạng thái thường và ta không muốn ghi thêm một byte vào bảng append-only cho ca phổ biến.
     */
    mapped: z.literal(true).optional(),
  })
  .strict()
  .refine((s) => s.end > s.start, { message: "end phải > start", path: ["end"] });
export type AiSpan = z.infer<typeof aiSpanSchema>;

export const aiEnvelopeSchema = z
  .object({
    kind: z.enum(AI_ENVELOPE_KINDS),
    /** Trang được chọn — BẮT BUỘC khi `kind: "page"`, CẤM khi `cancel`/`unknown`. */
    page: z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE).optional(),
    spans: z.array(aiSpanSchema).max(AI_ENVELOPE_MAX_SPANS).readonly(),
    /** Độ chắc của Người phiên dịch [0, 1]. Người làm dùng ngưỡng; LLM không tự quyết bằng số này. */
    confidence: z.number().min(0).max(1),
    /** Kẹt giữa đúng hai trang ⇒ hai id để hỏi A hay B. Rỗng khi không kẹt. */
    ambiguities: z.array(z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE)).max(AI_ENVELOPE_MAX_AMBIGUITIES).readonly(),
    /** Việc CHEN trong cùng câu (`@UC0165`): Người phiên dịch chỉ BÁO, thứ tự xử lý là của quy trình. */
    multi: z.array(z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE)).max(AI_ENVELOPE_MAX_MULTI).readonly(),
  })
  .strict()
  .superRefine((env, ctx) => {
    if (env.kind === "page" && env.page === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["page"], message: "kind=page phải có page" });
    }
    if ((env.kind === "cancel" || env.kind === "unknown") && env.page !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["page"], message: `kind=${env.kind} không mang page` });
    }
    if (env.kind === "cancel" && env.spans.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["spans"], message: "huỷ là tuyệt đối — không kèm span" });
    }
    if (env.ambiguities.length === 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["ambiguities"], message: "kẹt thì phải có ĐÚNG HAI ứng viên (A hay B), một ứng viên là đã chọn" });
    }
    if (new Set(env.ambiguities).size !== env.ambiguities.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["ambiguities"], message: "hai ứng viên phải khác nhau" });
    }
    if (env.page !== undefined && env.ambiguities.includes(env.page)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["ambiguities"], message: "trang đã chọn không đồng thời là ứng viên kẹt" });
    }
  });

export type AiEnvelope = z.infer<typeof aiEnvelopeSchema>;
